import { Award, Brush, GraduationCap, RefreshCcw, ShieldCheck } from "lucide-react";
import { FadeUp, MaskLineInView } from "./motion";

const ATELIER =
  "https://static.prod-images.emergentagent.com/jobs/cf31359b-b456-44db-bc7f-bfef23c871ef/images/dbfa5b7962da0f9166c5714c51f64c9a26cf75d7b9218c6a5b75ba7dd2e44d6b.jpeg";

const CERT_BODIES = ["Microsoft", "Apple", "Google", "CompTIA"];

const CRISIS_CREDENTIALS = [
  "Certified Crisis Intervention Specialist (CCIS)",
  "Certified Suicide Prevention Specialist",
  "Emergency Mental Health First Responder",
];

const EDUCATION = [
  {
    degree: "PhD — Business Psychology",
    school: "University of Texas · In progress",
    note: "Perfect 4.0 GPA",
  },
  {
    degree: "BS — Organizational Leadership",
    school: "University of Texas at Tyler",
    note: "Top 1% globally — ACBSP exam",
  },
  {
    degree: "Associate — IT Hardware & Software Design",
    school: "Purdue University",
    note: "",
  },
];

function Card({ index, icon: Icon, title, children, className = "" }) {
  return (
    <FadeUp
      delay={index * 0.08}
      className={`group border border-line bg-ink2 p-8 transition-colors duration-500 hover:border-vermilion/40 ${className}`}
    >
      <div className="mb-8 flex items-center justify-between">
        <Icon className="h-5 w-5 text-vermilion" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
          N.0{index}
        </span>
      </div>
      <h3 className="mb-4 font-serif text-2xl font-normal text-bone">
        {title}
      </h3>
      {children}
    </FadeUp>
  );
}

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-manifesto-section"
      className="relative border-t border-line py-20 sm:py-28"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
        <FadeUp className="mb-16 flex items-end justify-between gap-6">
          <div>
            <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-vermilion">
              01 — The Scholar
            </p>
            <h2 className="font-serif text-4xl font-light leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
              <MaskLineInView>The mind behind</MaskLineInView>
              <MaskLineInView delay={0.08}>
                Cultural Behavioral
              </MaskLineInView>
              <MaskLineInView delay={0.16}>
                Psychology<span className="text-vermilion">.</span>
              </MaskLineInView>
            </h2>
          </div>
          <span className="hidden font-serif text-2xl italic text-smoke md:block">
            (n. CBP)
          </span>
        </FadeUp>

        <div className="mb-16 grid gap-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <p className="mb-8 text-xl leading-relaxed text-bone sm:text-2xl">
              An American author and psychology researcher known for bridging{" "}
              <span className="text-vermilion">behavioral economics</span> and{" "}
              <span className="text-vermilion">multicultural psychology</span>{" "}
              — work credited with introducing the field of Cultural Behavioral
              Psychology.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="space-y-6 lg:col-span-7">
            <p className="max-w-2xl text-base leading-relaxed text-ash sm:text-lg">
              At 35, Forbes presides over an eponymous media empire spanning
              both the publishing and technology sectors — a polymathic career
              operating at the dynamic intersection of technology, psychology,
              and the arts.
            </p>
            <p className="max-w-2xl text-base leading-relaxed text-ash sm:text-lg">
              His trajectory is famously nonlinear: once a high school dropout,
              he went on to earn advanced degrees in engineering and
              organizational leadership, maintain a perfect 4.0 GPA as a
              doctoral candidate, and score in the top 1% globally on the ACBSP
              outbound exam for business knowledge.
            </p>
          </FadeUp>
        </div>

        <div
          data-testid="research-bento-grid"
          className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          <Card index={1} icon={RefreshCcw} title="The Polymath Arc" className="lg:col-span-2">
            <p className="text-base leading-relaxed text-ash">
              From high school dropout to advanced degrees in engineering and
              organizational leadership — and now a PhD candidate in Business
              Psychology at the University of Texas. A career built on the
              conviction that disciplines are doors, not walls.
            </p>
          </Card>

          <Card index={2} icon={GraduationCap} title="Education">
            <ul className="space-y-5">
              {EDUCATION.map((e) => (
                <li key={e.degree} className="border-l border-line pl-4">
                  <p className="text-sm font-medium text-bone">{e.degree}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                    {e.school}
                  </p>
                  {e.note && (
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-vermilion">
                      {e.note}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </Card>

          <Card index={3} icon={Award} title="Nine National Certifications" className="lg:col-span-1">
            <p className="mb-6 text-base leading-relaxed text-ash">
              Nine national certifications from leading technology
              corporations.
            </p>
            <div className="flex flex-wrap gap-2">
              {CERT_BODIES.map((c) => (
                <span
                  key={c}
                  className="border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ash"
                >
                  {c}
                </span>
              ))}
            </div>
          </Card>

          <Card index={4} icon={ShieldCheck} title="Crisis & Care Credentials" className="lg:col-span-1">
            <ul className="space-y-4">
              {CRISIS_CREDENTIALS.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-3 text-sm leading-relaxed text-ash"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-vermilion" />
                  {c}
                </li>
              ))}
            </ul>
          </Card>

          <FadeUp
            delay={0.4}
            className="group relative overflow-hidden border border-line transition-colors duration-500 hover:border-vermilion/40 lg:col-span-1"
          >
            <img
              src={ATELIER}
              alt="Abstract painting — black ink and vermilion"
              className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
            <div className="relative flex h-full flex-col justify-end p-8">
              <Brush className="mb-6 h-5 w-5 text-vermilion" strokeWidth={1.5} />
              <h3 className="mb-4 font-serif text-2xl font-normal text-bone">
                The Visual Artist
              </h3>
              <p className="text-base leading-relaxed text-ash">
                An avid photographer and painter, Forbes treats these outlets
                as essential to his technical pursuits — a medium to study
                <span className="font-serif italic text-bone">
                  {" "}
                  “light, color, and emotion.”
                </span>
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}